import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import * as libraryService from "../service/libraryService";
import type { FetchBooksParams } from "../service/libraryService";
import type { LibraryState } from "../types";
import i18n from "@/i18n";

export const fetchBooks = createAsyncThunk(
  "library/fetchBooks",
  async ({ institutionId, params }: { institutionId: number; params?: FetchBooksParams }) => {
    return await libraryService.fetchBooks(institutionId, params);
  }
);

export const createBook = createAsyncThunk(
  "library/createBook",
  async (
    {
      institutionId,
      payload,
    }: {
      institutionId: number;
      payload: { title: string; author?: string; isbn?: string; category?: string; shelfLocation?: string; coverUrl?: string; totalCopies?: number };
    },
    { dispatch }
  ) => {
    await libraryService.createBook(institutionId, payload);
    await dispatch(fetchBooks({ institutionId }));
  }
);

export const updateBook = createAsyncThunk(
  "library/updateBook",
  async (
    {
      institutionId,
      bookId,
      payload,
    }: {
      institutionId: number;
      bookId: number;
      payload: { title?: string; author?: string; isbn?: string; category?: string; shelfLocation?: string; coverUrl?: string; totalCopies?: number };
    },
    { dispatch }
  ) => {
    await libraryService.updateBook(institutionId, bookId, payload);
    await dispatch(fetchBooks({ institutionId }));
  }
);

export const deleteBook = createAsyncThunk(
  "library/deleteBook",
  async ({ institutionId, bookId }: { institutionId: number; bookId: number }, { dispatch }) => {
    await libraryService.deleteBook(institutionId, bookId);
    await dispatch(fetchBooks({ institutionId }));
  }
);

export const fetchCopies = createAsyncThunk(
  "library/fetchCopies",
  async ({ institutionId, bookId }: { institutionId: number; bookId: number }) => {
    const copies = await libraryService.fetchCopies(institutionId, bookId);
    return { bookId, copies };
  }
);

export const updateCopyStatus = createAsyncThunk(
  "library/updateCopyStatus",
  async (
    {
      institutionId,
      bookId,
      copyId,
      status,
    }: { institutionId: number; bookId: number; copyId: number; status: "AVAILABLE" | "LOST" | "DAMAGED" },
    { dispatch }
  ) => {
    await libraryService.updateCopyStatus(institutionId, bookId, copyId, status);
    await dispatch(fetchCopies({ institutionId, bookId }));
    await dispatch(fetchBooks({ institutionId }));
  }
);

export const issueBook = createAsyncThunk(
  "library/issueBook",
  async (
    {
      institutionId,
      payload,
    }: { institutionId: number; payload: { bookId: number; studentId?: number; staffId?: number; dueDate: string; academicYearId?: number } },
    { dispatch }
  ) => {
    await libraryService.issueBook(institutionId, payload);
    await dispatch(fetchBooks({ institutionId }));
  }
);

export const returnBook = createAsyncThunk(
  "library/returnBook",
  async (
    {
      institutionId,
      issueId,
      studentId,
      staffId,
      academicYearId,
    }: { institutionId: number; issueId: number; studentId?: number; staffId?: number; academicYearId?: number },
    { dispatch }
  ) => {
    await libraryService.returnBook(institutionId, issueId);
    await dispatch(fetchBooks({ institutionId }));
    if (studentId) await dispatch(fetchStudentIssues({ institutionId, studentId, academicYearId }));
    if (staffId) await dispatch(fetchStaffIssues({ institutionId, staffId }));
    await dispatch(fetchOverdue(institutionId));
  }
);

export const renewIssue = createAsyncThunk(
  "library/renewIssue",
  async (
    {
      institutionId,
      issueId,
      newDueDate,
      studentId,
      staffId,
      academicYearId,
    }: { institutionId: number; issueId: number; newDueDate: string; studentId?: number; staffId?: number; academicYearId?: number },
    { dispatch }
  ) => {
    await libraryService.renewIssue(institutionId, issueId, newDueDate);
    if (studentId) await dispatch(fetchStudentIssues({ institutionId, studentId, academicYearId }));
    if (staffId) await dispatch(fetchStaffIssues({ institutionId, staffId }));
    await dispatch(fetchOverdue(institutionId));
  }
);

export const fetchStudentIssues = createAsyncThunk(
  "library/fetchStudentIssues",
  async ({ institutionId, studentId, academicYearId }: { institutionId: number; studentId: number; academicYearId?: number }) => {
    return await libraryService.fetchStudentIssues(institutionId, studentId, academicYearId);
  }
);

export const fetchStaffIssues = createAsyncThunk(
  "library/fetchStaffIssues",
  async ({ institutionId, staffId }: { institutionId: number; staffId: number }) => {
    return await libraryService.fetchStaffIssues(institutionId, staffId);
  }
);

export const fetchOverdue = createAsyncThunk("library/fetchOverdue", async (institutionId: number) => {
  return await libraryService.fetchOverdue(institutionId);
});

export const fetchSettings = createAsyncThunk("library/fetchSettings", async (institutionId: number) => {
  return await libraryService.fetchSettings(institutionId);
});

export const updateSettings = createAsyncThunk(
  "library/updateSettings",
  async ({ institutionId, finePerDay }: { institutionId: number; finePerDay: number }) => {
    return await libraryService.updateSettings(institutionId, finePerDay);
  }
);

const initialState: LibraryState = {
  books: [],
  categories: [],
  pagination: { page: 1, limit: 20, total: 0, totalPages: 1 },
  studentIssues: [],
  staffIssues: [],
  overdue: [],
  settings: null,
  copiesByBook: {},
  loading: false,
  saving: false,
  error: null,
};

const librarySlice = createSlice({
  name: "library",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchBooks.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBooks.fulfilled, (state, action) => {
        state.loading = false;
        state.books = action.payload.books;
        state.categories = action.payload.categories;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchBooks.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message ?? i18n.t("facilities:errors.failedToLoadBooks");
      })
      .addCase(createBook.rejected, (state, action) => {
        state.error = action.error.message ?? i18n.t("facilities:errors.failedToAddBook");
      })
      .addCase(updateBook.rejected, (state, action) => {
        state.error = action.error.message ?? i18n.t("facilities:errors.failedToUpdateBook");
      })
      .addCase(deleteBook.rejected, (state, action) => {
        state.error = action.error.message ?? i18n.t("facilities:errors.failedToDeleteBook");
      })
      .addCase(fetchCopies.fulfilled, (state, action) => {
        state.copiesByBook[action.payload.bookId] = action.payload.copies;
      })
      .addCase(updateCopyStatus.rejected, (state, action) => {
        state.error = action.error.message ?? i18n.t("facilities:errors.failedToUpdateCopyStatus");
      })
      .addCase(issueBook.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(issueBook.fulfilled, (state) => {
        state.saving = false;
      })
      .addCase(issueBook.rejected, (state, action) => {
        state.saving = false;
        state.error = action.error.message ?? i18n.t("facilities:errors.failedToIssueBook");
      })
      .addCase(returnBook.rejected, (state, action) => {
        state.error = action.error.message ?? i18n.t("facilities:errors.failedToReturnBook");
      })
      .addCase(renewIssue.rejected, (state, action) => {
        state.error = action.error.message ?? i18n.t("facilities:errors.failedToRenewIssue");
      })
      .addCase(fetchStudentIssues.fulfilled, (state, action) => {
        state.studentIssues = action.payload;
      })
      .addCase(fetchStaffIssues.fulfilled, (state, action) => {
        state.staffIssues = action.payload;
      })
      .addCase(fetchOverdue.fulfilled, (state, action) => {
        state.overdue = action.payload;
      })
      .addCase(fetchSettings.fulfilled, (state, action) => {
        state.settings = action.payload;
      })
      .addCase(updateSettings.fulfilled, (state, action) => {
        state.settings = action.payload;
      });
  },
});

export default librarySlice.reducer;
